/**
 * What the PDF fidelity stack read off the page: page size, margins, body
 * font, heading tiers, running header/footer. This is the same profile the
 * drafting agent gets as `<name>.layout.json`, shown so the admin can see the
 * agent will reproduce the right thing.
 */
import type { FC } from "react";

import type { LayoutProfile } from "../types";

const pt = (value: number | undefined | null): string =>
  value === undefined || value === null ? "–" : `${Math.round(value)} pt`;

const inches = (value: number | undefined | null): string =>
  value === undefined || value === null ? "" : ` (${(value / 72).toFixed(2)}")`;

const Row: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => (
  <div className="grid grid-cols-[7rem_1fr] gap-2 py-1 text-sm">
    <dt className="text-fg-3">{label}</dt>
    <dd className="min-w-0 break-words">{children}</dd>
  </div>
);

export const LayoutPanel: FC<{ layout: LayoutProfile }> = ({ layout }) => {
  const page = layout.page ?? {};
  const margins = layout.margins ?? {};
  const body = layout.fonts?.body ?? {};
  const tiers = layout.heading_tiers ?? [];
  const lists = layout.lists ?? {};
  const tables = Array.isArray(layout.tables) ? layout.tables.length : 0;
  const footnotes = Array.isArray(layout.footnotes)
    ? layout.footnotes.length
    : 0;

  return (
    <dl className="divide-y divide-border" data-testid="layout-panel">
      <Row label="Page">
        {page.width && page.height
          ? `${Math.round(page.width)} × ${Math.round(page.height)} pt${
              page.orientation ? `, ${page.orientation}` : ""
            }`
          : "–"}
      </Row>
      <Row label="Margins">
        {["top", "right", "bottom", "left"]
          .map((side) => {
            const value = margins[side as keyof typeof margins];
            return `${side[0].toUpperCase()} ${pt(value)}${inches(value)}`;
          })
          .join(" · ")}
      </Row>
      <Row label="Body">
        {body.font ?? "unknown font"} {body.size ? `${body.size} pt` : ""}
        {layout.fonts?.line_spacing
          ? `, line spacing ×${layout.fonts.line_spacing}`
          : ""}
        {layout.body_alignment ? `, ${layout.body_alignment}-aligned` : ""}
      </Row>
      {tiers.length ? (
        <Row label="Headings">
          <ul className="space-y-0.5">
            {tiers.map((tier) => (
              <li key={tier.tier}>
                H{tier.tier}: {tier.size} pt{tier.bold ? " bold" : ""}
                {tier.alignment ? `, ${tier.alignment}` : ""}
                {tier.samples?.[0] ? (
                  <span className="text-fg-3"> — “{tier.samples[0]}”</span>
                ) : null}
              </li>
            ))}
          </ul>
        </Row>
      ) : null}
      {lists.numbered_items || lists.bullet_items ? (
        <Row label="Lists">
          {lists.numbered_items ?? 0} numbered · {lists.bullet_items ?? 0}{" "}
          bullet items
        </Row>
      ) : null}
      {tables || footnotes ? (
        <Row label="Structure">
          {[
            tables ? `${tables} table${tables === 1 ? "" : "s"}` : null,
            footnotes
              ? `${footnotes} footnote${footnotes === 1 ? "" : "s"}`
              : null
          ]
            .filter(Boolean)
            .join(" · ")}
        </Row>
      ) : null}
      {layout.header ? <Row label="Header">“{layout.header}”</Row> : null}
      {layout.footer ? <Row label="Footer">“{layout.footer}”</Row> : null}
      {layout.numbering?.position ? (
        <Row label="Page numbers">{layout.numbering.position}</Row>
      ) : null}
      {layout.signature_block ? (
        <Row label="Signature">Signature block on the last page</Row>
      ) : null}
    </dl>
  );
};
